import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { messages } from "./messages";
import { BackButton } from "components/BackButton";
import { useParams } from "react-router-dom";
import { TotalBalancePlatformCard } from "./components/TotalBalancePlatformCard";
import LoadingAnimation from "components/LoadingAnimation";
import { TotalBalanceLocalMarketCard } from "./components/TotalBalanceLocalMarketCard";
import { TotalBalanceGlobalMarketCard } from "./components/TotalBalanceGlobalMarketCard";
import { INTRODUCING_BROKER_MEMBER_PILL } from "./types";
import { HistoryInfiniteScroll } from "components/HistoryInfiniteScroll";
import { BymaActivitiesScroll } from "components/BymaActivitiesScroll";
import AlpacaActivitiesScroll from "components/AlpacaActivitiesScroll";
import { Pill } from "components/Pill";
import { useGetMembers } from "hooks/partnerApi/useGetMembers";
import BigNumber from "bignumber.js";
import { FormatedValue } from "components/FormatedValue";
import { useCurrency } from "hooks/user/useCurrencies";
import { DashboardSlider } from "components/DashboardSlider";

const HistoryComponent: React.FC<{
  historyType: INTRODUCING_BROKER_MEMBER_PILL;
  header: JSX.Element;
  headerSticky?: JSX.Element;
  memberUid: string;
}> = ({ historyType, header, headerSticky, memberUid }) => {
  switch (historyType) {
    case INTRODUCING_BROKER_MEMBER_PILL.PLATFORM:
      return (
        <HistoryInfiniteScroll
          header={header}
          noChips
          headerSticky={headerSticky}
          memberUid={memberUid}
        />
      );
    case INTRODUCING_BROKER_MEMBER_PILL.LOCAL_MARKET:
      return (
        <BymaActivitiesScroll
          header={header}
          noChips
          headerSticky={headerSticky}
          memberUid={memberUid}
        />
      );
    case INTRODUCING_BROKER_MEMBER_PILL.GLOBAL_MARKET:
      return (
        <AlpacaActivitiesScroll
          header={header}
          noChips
          headerSticky={headerSticky}
          memberUid={memberUid}
        />
      );
    default:
      return null;
  }
};

export const SingleMemberPage: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();

  const [currentPill, setCurrentPill] =
    React.useState<INTRODUCING_BROKER_MEMBER_PILL>(
      INTRODUCING_BROKER_MEMBER_PILL.PLATFORM,
    );

  const { data: members } = useGetMembers();
  const currencyUsd = useCurrency("usd");

  const { member, name, totalBalance } = useMemo(() => {
    const member = members?.flat().find((member) => member.uid === uid);
    const memberName =
      member?.natural_person_data?.first_name +
        " " +
        member?.natural_person_data?.last_name || "";
    const totalBalance = BigNumber.sum(
      member?.platform_balance || "0",
      member?.local_market_balance || "0",
      member?.global_market_balance || "0",
    ).toString();
    return {
      member,
      name: memberName,
      totalBalance,
    };
  }, [members, uid]);

  return uid ? (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="mb-2 flex flex-row items-center justify-between border-b border-[--milkylb-color-secondary-100]">
        <div className="mb-8 flex flex-col items-start justify-normal">
          <BackButton className="mb-4" />
          <h1 className="font-['Tomato_Grotesk'] text-4xl font-bold">{name}</h1>
        </div>
      </div>
      <div className="flex flex-col gap-y-2 pb-4 pt-4 font-['Tomato_Grotesk']">
        <p className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
          <FormattedMessage {...messages.totalBalance} />
        </p>
        <p className="text-4xl font-bold text-[--milkylb-color-neutral-800]">
          <FormatedValue value={totalBalance} />{" "}
          {currencyUsd?.ticker_id.toUpperCase()}
        </p>
      </div>
      <HistoryComponent
        memberUid={uid}
        historyType={currentPill}
        header={
          <div className="flex w-full flex-col">
            <DashboardSlider widgetWidth={385} className="-ml-8">
              <TotalBalancePlatformCard
                totalBalance={member?.platform_balance ?? ""}
                currency={member?.platform_currency}
                uid={uid}
              />
              <TotalBalanceLocalMarketCard
                disabled={!member?.local_market_account_active}
                totalBalance={member?.local_market_balance ?? ""}
                currency={member?.local_market_currency}
                uid={uid}
              />
              <TotalBalanceGlobalMarketCard
                disabled={!member?.global_market_account_active}
                totalBalance={member?.global_market_balance ?? ""}
                currency={member?.global_market_currency}
                uid={uid}
              />
            </DashboardSlider>
            <div className="-ml-2 flex grow flex-row items-center justify-between border-b-4 border-l-8 border-r-8 border-t-8 border-white bg-white">
              <h3 className="mr-16 flex shrink-0 cursor-pointer items-center font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.lastMovements} />
              </h3>
              <div className="flex w-full flex-row justify-end gap-x-6 font-normal">
                <Pill
                  selected={
                    currentPill === INTRODUCING_BROKER_MEMBER_PILL.PLATFORM
                  }
                  onClick={() =>
                    setCurrentPill(INTRODUCING_BROKER_MEMBER_PILL.PLATFORM)
                  }
                >
                  <FormattedMessage {...messages.platformPill} />
                </Pill>
                {member?.local_market_account_active && (
                  <Pill
                    selected={
                      currentPill ===
                      INTRODUCING_BROKER_MEMBER_PILL.LOCAL_MARKET
                    }
                    onClick={() =>
                      setCurrentPill(
                        INTRODUCING_BROKER_MEMBER_PILL.LOCAL_MARKET,
                      )
                    }
                  >
                    <FormattedMessage {...messages.localMarketPill} />
                  </Pill>
                )}
                {member?.global_market_account_active && (
                  <Pill
                    selected={
                      currentPill ===
                      INTRODUCING_BROKER_MEMBER_PILL.GLOBAL_MARKET
                    }
                    onClick={() =>
                      setCurrentPill(
                        INTRODUCING_BROKER_MEMBER_PILL.GLOBAL_MARKET,
                      )
                    }
                  >
                    <FormattedMessage {...messages.globalMarketPill} />
                  </Pill>
                )}
              </div>
            </div>
          </div>
        }
      />
    </div>
  ) : (
    <LoadingAnimation />
  );
};
